import React, { useState, useEffect } from 'react';
import { Check, Loader2, CreditCard, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { membershipsApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map membership names to package IDs
const PACKAGE_IDS = {
  'Glow Starter': 'glow-starter',
  'Radiance Plus': 'radiance-plus',
  'Elite Beauty': 'elite-beauty'
};

// Payment method icons
const PaymentIcons = {
  stripe: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
    </svg>
  ),
  paypal: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
    </svg>
  ),
  apple: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
    </svg>
  )
};

const Memberships = () => {
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const data = await membershipsApi.getAll();
      setMemberships(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching memberships:', err);
      setError('Failed to load memberships. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openPaymentModal = (membership) => {
    setSelectedMembership(membership);
    setShowPaymentModal(true);
  };

  const handlePayment = async (paymentMethod) => {
    if (!selectedMembership) return;
    
    const packageId = PACKAGE_IDS[selectedMembership.name];
    
    if (!packageId) {
      toast({
        title: 'Error',
        description: 'Invalid membership package',
        variant: 'destructive'
      });
      return;
    }

    setProcessingPayment(paymentMethod);

    try {
      let endpoint = '';
      let redirectKey = '';
      
      if (paymentMethod === 'stripe' || paymentMethod === 'apple') {
        endpoint = `${BACKEND_URL}/api/payments/checkout/membership`;
        redirectKey = 'checkout_url';
      } else if (paymentMethod === 'paypal') {
        endpoint = `${BACKEND_URL}/api/payments/paypal/checkout/membership`;
        redirectKey = 'approval_url';
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_type: 'membership',
          package_id: packageId,
          origin_url: window.location.origin,
          practiceId: localStorage.getItem('practiceId') || 'demo-practice-001',
          user_email: user?.email || '',
          user_name: user?.name || ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      console.log('Payment response:', data);

      // Redirect to payment provider
      const redirectUrl = data[redirectKey];
      if (redirectUrl) {
        console.log('Redirecting to:', redirectUrl);
        setShowPaymentModal(false);
        setProcessingPayment(null);
        
        // Open in new tab - more reliable across browsers
        window.open(redirectUrl, '_blank');
        
        toast({
          title: 'Payment page opened',
          description: 'Complete your payment in the new tab.',
        });
      } else {
        console.error('No redirect URL in response:', data);
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setRedirecting(false);
      toast({
        title: 'Payment Error',
        description: err.message || 'Failed to initiate payment. Please try again.',
        variant: 'destructive'
      });
      setProcessingPayment(null);
    }
  };

  // Show redirecting overlay
  if (redirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Redirecting to Payment...</h2>
          <p className="text-gray-600">Please wait while we connect you to the payment provider.</p>
        </div>
      </div>
    );
  }

  const PaymentModal = () => {
    if (!showPaymentModal || !selectedMembership) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
          <button 
            onClick={() => {
              setShowPaymentModal(false);
              setSelectedMembership(null);
              setProcessingPayment(null);
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Payment Method</h2>
          <p className="text-gray-600 mb-6">
            {selectedMembership.name} - ${selectedMembership.price}/month
          </p>
          
          <div className="space-y-3">
            {/* Credit/Debit Card (Stripe) */}
            <button
              onClick={() => handlePayment('stripe')}
              disabled={processingPayment}
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="text-gray-700">{PaymentIcons.stripe}</div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Credit / Debit Card</p>
                  <p className="text-sm text-gray-500">Visa, Mastercard, Amex</p>
                </div>
              </div>
              {processingPayment === 'stripe' && <Loader2 className="h-5 w-5 animate-spin" />}
            </button>

            {/* PayPal */}
            <button
              onClick={() => handlePayment('paypal')}
              disabled={processingPayment}
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-[#0070ba] hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="text-[#0070ba]">{PaymentIcons.paypal}</div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">PayPal</p>
                  <p className="text-sm text-gray-500">Pay with your PayPal account</p>
                </div>
              </div>
              {processingPayment === 'paypal' && <Loader2 className="h-5 w-5 animate-spin" />}
            </button>

            {/* Apple Pay (through Stripe) */}
            <button
              onClick={() => handlePayment('apple')}
              disabled={processingPayment}
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-gray-900 hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <div className="text-gray-900">{PaymentIcons.apple}</div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Apple Pay</p>
                  <p className="text-sm text-gray-500">Fast checkout with Apple Pay</p>
                </div>
              </div>
              {processingPayment === 'apple' && <Loader2 className="h-5 w-5 animate-spin" />}
            </button>

            {/* CareCredit - External Link */}
            <a
              href="https://www.carecredit.com/apply/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-gray-50 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">CC</div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">CareCredit</p>
                  <p className="text-sm text-gray-500">Healthcare financing</p>
                </div>
              </div>
              <span className="text-xs text-gray-400">External</span>
            </a>

            {/* Gift Card - Boulevard handles this */}
            <a
              href="#book-now"
              onClick={() => setShowPaymentModal(false)}
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-gray-50 transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">GC</div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Gift Card</p>
                  <p className="text-sm text-gray-500">Redeem via booking system</p>
                </div>
              </div>
            </a>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6">
            Secure payment processing. Your information is encrypted.
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-lg">Loading membership plans...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex flex-col items-center justify-center py-32">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchMemberships}>Try Again</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Membership Plans
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Build your beauty routine with flexible memberships. All plans include credit rollover—no more losing unused credits!
          </p>
        </div>

        {/* Membership Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {memberships.map((membership) => (
            <Card
              key={membership.id}
              className={`relative transition-all duration-300 hover:shadow-xl ${
                membership.popular
                  ? 'border-primary border-2 scale-105'
                  : 'border-gray-200 hover:border-primary'
              }`}
            >
              {membership.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-white px-4 py-1">
                    Most Popular
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl mb-2">{membership.name}</CardTitle>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gray-900">${membership.price}</span>
                  <span className="text-gray-600">/month</span>
                </div>
                <CardDescription className="text-lg">
                  {membership.credits} credits monthly
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {membership.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button
                  className={`w-full ${
                    membership.popular
                      ? ''
                      : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                  onClick={() => openPaymentModal(membership)}
                  data-testid={`join-membership-${membership.id}`}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Join {membership.name}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Rollover Feature Highlight */}
        <div className="bg-secondary rounded-3xl p-12 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Never Lose Your Credits
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
            All unused credits automatically roll over to the next month. Build your credit balance and use them whenever you're ready for your next treatment.
          </p>
          <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-md">
            <Check className="h-5 w-5 text-primary mr-2" />
            <span className="font-semibold text-gray-900">Rollover Capability Included</span>
          </div>
        </div>
      </div>

      <Footer />
      
      {/* Payment Method Modal */}
      <PaymentModal />
    </div>
  );
};

export default Memberships;